/**
 * Pagination Controls - Go to Page functionality + Mobile Dropdown
 * Handles page number input validation, navigation, and mobile dropdown
 * 
 * Features:
 * - Desktop "Go to page" input with validation
 * - Mobile dropdown pagination selector
 * - Integration with on_click_handler for AJAX navigation
 */

(function() {
  'use strict';

  const SELECTORS = {
    goToButton: '.pagination-go-to__button',
    goToInput: '.pagination-go-to__input',
    goToError: '.pagination-go-to__error',
    goToContainer: '.pagination-go-to',
    goToHover: '.pagination-go-to--hover',
    goToGapItem: '.pagination__item--gap-with-go-to',
    goToGapClickable: '.pagination__link--gap-clickable',
    dropdownContainer: '.pagination__item--dropdown',
    dropdownToggle: '.pagination__link--dropdown-toggle',
    dropdownMenu: '.pagination__dropdown-menu',
    dropdownItem: '.pagination__dropdown-item'
  };

  const DATA_ATTRIBUTES = {
    totalPages: 'data-total-pages',
    currentPage: 'data-current-page',
    baseUrl: 'data-base-url',
    onClickHandler: 'data-on-click-handler',
    page: 'data-page',
    href: 'data-href',
    sectionId: 'data-section-id'
  };

  const ARIA_STATES = {
    expanded: 'aria-expanded',
    disabled: 'aria-disabled',
    hidden: 'hidden'
  };

  const initializedDropdowns = new WeakSet();
  const selectorCache = new Map();

  /**
   * Cache DOM selector lookups for performance
   * @param {string} selector - CSS selector
   * @returns {NodeList} Cached query result
   */
  function querySelectorCached(selector) {
    if (!selectorCache.has(selector)) {
      selectorCache.set(selector, document.querySelectorAll(selector));
    }
    return selectorCache.get(selector);
  }

  /**
   * Clears selector cache on DOM mutations
   */
  function clearSelectorCache() {
    selectorCache.clear();
  }

  /**
   * Validates page number input
   * @param {number} pageNumber - The page number to validate
   * @param {number} totalPages - Total number of pages
   * @returns {boolean} True if valid, false otherwise
   */
  function isValidPageNumber(pageNumber, totalPages) {
    return !isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages;
  }

  /**
   * Shows error message for invalid input
   * @param {HTMLElement} input - The input element
   * @param {number} totalPages - Total number of pages
   */
  function showInputError(input, totalPages) {
    const container = input.closest(SELECTORS.goToContainer);
    if (!container) return;

    const errorDiv = container.querySelector(SELECTORS.goToError);
    if (errorDiv) {
      errorDiv.textContent = `Please enter a number between 1 and ${totalPages}`;
      errorDiv.hidden = false;
    }
    input.focus();
  }

  /**
   * Hides error message
   * @param {HTMLElement} input - The input element
   */
  function hideInputError(input) {
    const container = input.closest(SELECTORS.goToContainer);
    if (!container) return;

    const errorDiv = container.querySelector(SELECTORS.goToError);
    if (errorDiv) {
      errorDiv.hidden = true;
    }
  }

  /**
   * Builds page URL from current location preserving all query parameters
   * This matches Horizon's PaginatedList behavior
   * @param {string} baseUrl - Base URL (ignored, kept for backwards compatibility)
   * @param {number} pageNumber - Page number
   * @returns {string} Complete page URL
   */
  function buildPageUrl(baseUrl, pageNumber) {
    const url = new URL(window.location.href);
    
    if (pageNumber >= 1) {
      url.searchParams.set('page', String(pageNumber));
    }
    
    url.hash = '';
    
    return url.toString();
  }

  /**
   * Navigates to a page using component handler or direct navigation
   * @param {string} pageUrl - The URL to navigate to (fallback only)
   * @param {string} onClickHandler - Optional handler function path
   * @param {number} pageNumber - Page number for handler
   * @param {HTMLElement} sourceElement - Element to find component from
   */
  function navigateToPage(pageUrl, onClickHandler, pageNumber, sourceElement = null) {
    if (onClickHandler && sourceElement) {
      try {
        const resultsListComponent = sourceElement.closest('results-list');
        
        if (resultsListComponent) {
          const handlerName = onClickHandler.replace(/^\//, '');
          
          if (typeof resultsListComponent[handlerName] === 'function') {
            // Prefer component handler to keep AJAX pagination behavior.
            const fakeEvent = new Event('click', { bubbles: false, cancelable: true });
            resultsListComponent[handlerName]({ page: pageNumber.toString() }, fakeEvent);
            return;
          }
        }
      } catch (error) {
        console.error('Error calling pagination handler:', error);
      }
    }

    window.location.href = pageUrl;
  }

  /**
   * Handles "Go to page" button click
   * @param {HTMLElement} button - The button element
   */
  function handleGoToPage(button) {
    const input = button.previousElementSibling;
    
    if (!input || !input.classList.contains('pagination-go-to__input')) {
      return;
    }

    const totalPages = parseInt(input.getAttribute(DATA_ATTRIBUTES.totalPages), 10);
    const pageNumber = parseInt(input.value, 10);
    const onClickHandler = input.getAttribute(DATA_ATTRIBUTES.onClickHandler);
    const baseUrl = input.getAttribute(DATA_ATTRIBUTES.baseUrl);
    const logicalGoTo = input.getAttribute('data-hybrid-logical-go-to') === 'true';
    const subpages = parseInt(input.getAttribute('data-hybrid-subpages') || '0', 10);

    if (logicalGoTo && Number.isFinite(subpages) && subpages > 0) {
      if (!isValidPageNumber(pageNumber, totalPages)) {
        showInputError(input, totalPages);
        return;
      }
      hideInputError(input);
      const pageUrl = buildPageUrl(baseUrl, pageNumber);
      navigateToPage(pageUrl, onClickHandler, pageNumber, button);
      return;
    }

    if (!isValidPageNumber(pageNumber, totalPages)) {
      showInputError(input, totalPages);
      return;
    }

    hideInputError(input);
    const pageUrl = buildPageUrl(baseUrl, pageNumber);
    navigateToPage(pageUrl, onClickHandler, pageNumber, button);
  }

  /**
   * Closes all dropdown menus
   */
  function closeAllDropdowns() {
    const allDropdowns = document.querySelectorAll(SELECTORS.dropdownMenu);
    
    allDropdowns.forEach(dropdown => {
      if (dropdown.hasAttribute(ARIA_STATES.hidden)) return;
      dropdown.setAttribute(ARIA_STATES.hidden, '');
      dropdown.style.display = 'none';
      
      const toggle = dropdown.previousElementSibling;
      if (toggle?.classList.contains('pagination__link--dropdown-toggle')) {
        toggle.setAttribute(ARIA_STATES.expanded, 'false');
      }
    });
  }

  /**
   * Closes a specific dropdown menu
   * @param {HTMLElement} dropdown - The dropdown element
   * @param {HTMLElement} toggle - The toggle button element
   */
  function closeDropdown(dropdown, toggle) {
    if (dropdown.hasAttribute(ARIA_STATES.hidden)) return;
    dropdown.setAttribute(ARIA_STATES.hidden, '');
    dropdown.style.display = 'none';
    toggle.setAttribute(ARIA_STATES.expanded, 'false');
  }

  /**
   * Opens a dropdown menu
   * @param {HTMLElement} dropdown - The dropdown element
   * @param {HTMLElement} toggle - The toggle button element
   */
  function openDropdown(dropdown, toggle) {
    closeAllDropdowns();
    
    if (dropdown.hasAttribute(ARIA_STATES.hidden)) {
      dropdown.removeAttribute(ARIA_STATES.hidden);
    }
    dropdown.style.display = 'block';
    toggle.setAttribute(ARIA_STATES.expanded, 'true');
  }

  /**
   * Handles dropdown toggle button click
   * @param {Event} event - Click event
   * @param {HTMLElement} toggle - Toggle button element
   * @param {HTMLElement} dropdown - Dropdown menu element
   */
  function handleDropdownToggle(event, toggle, dropdown) {
    event.preventDefault();
    event.stopPropagation();

    const isExpanded = toggle.getAttribute(ARIA_STATES.expanded) === 'true';

    if (isExpanded) {
      closeDropdown(dropdown, toggle);
    } else {
      openDropdown(dropdown, toggle);
    }
  }

  /**
   * Handles dropdown item click for navigation
   * @param {Event} event - Click event
   * @param {HTMLElement} item - Dropdown item element
   * @param {HTMLElement} dropdown - Dropdown menu element
   * @param {HTMLElement} toggle - Toggle button element
   */
  function handleDropdownItemClick(event, item, dropdown, toggle) {
    event.preventDefault();
    event.stopPropagation();

    const pageNumber = parseInt(item.getAttribute(DATA_ATTRIBUTES.page), 10);
    const onClickHandler = item.getAttribute(DATA_ATTRIBUTES.onClickHandler);
    
    const pageUrl = buildPageUrl('', pageNumber);
    navigateToPage(pageUrl, onClickHandler, pageNumber, item);
    closeDropdown(dropdown, toggle);
  }

  /**
   * Attaches direct event listeners to dropdown toggle button
   * This ensures clicks work immediately, even during page load
   * @param {HTMLElement} toggle - Toggle button element
   * @param {HTMLElement} dropdown - Dropdown menu element
   */
  function attachDropdownToggleListener(toggle, dropdown) {
    const newToggle = toggle.cloneNode(true);
    toggle.parentNode.replaceChild(newToggle, toggle);
    
    newToggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      if (!newToggle.hasAttribute(ARIA_STATES.expanded)) {
        newToggle.setAttribute(ARIA_STATES.expanded, 'false');
      }
      
      handleDropdownToggle(event, newToggle, dropdown);
    }, { capture: false, passive: false });
    
    return newToggle;
  }

  /**
   * Attaches direct event listeners to dropdown items
   * @param {HTMLElement} item - Dropdown item element
   * @param {HTMLElement} dropdown - Dropdown menu element
   * @param {HTMLElement} toggle - Toggle button element
   */
  function attachDropdownItemListener(item, dropdown, toggle) {
    item.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      handleDropdownItemClick(event, item, dropdown, toggle);
    }, { capture: false, passive: false });
  }

  /**
   * Initializes a single dropdown container immediately
   * Attaches direct event listeners for instant response
   * @param {HTMLElement} container - Dropdown container element
   */
  function initializeDropdownContainer(container) {
    if (initializedDropdowns.has(container)) {
      return true;
    }
    
    const toggle = container.querySelector(SELECTORS.dropdownToggle);
    const dropdown = container.querySelector(SELECTORS.dropdownMenu);
    
    if (!toggle || !dropdown) {
      return false;
    }
    
    if (!toggle.hasAttribute(ARIA_STATES.expanded)) {
      toggle.setAttribute(ARIA_STATES.expanded, 'false');
    }
    
    if (!dropdown.hasAttribute(ARIA_STATES.hidden)) {
      dropdown.setAttribute(ARIA_STATES.hidden, '');
      dropdown.style.display = 'none';
    }

    const newToggle = attachDropdownToggleListener(toggle, dropdown);

    const items = container.querySelectorAll(SELECTORS.dropdownItem);
    items.forEach(item => {
      attachDropdownItemListener(item, dropdown, newToggle);
    });
    
    initializedDropdowns.add(container);
    
    return true;
  }

  /**
   * Initializes all existing dropdown containers
   */
  function initializeAllDropdowns() {
    const containers = document.querySelectorAll(SELECTORS.dropdownContainer);
    containers.forEach(container => {
      initializeDropdownContainer(container);
    });
  }

  /**
   * Initializes mobile dropdown functionality
   * Unified event delegation handler for better performance
   */
  function initMobileDropdown() {
    // Single consolidated click handler for all dropdown interactions
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest(SELECTORS.dropdownToggle);
      const item = event.target.closest(SELECTORS.dropdownItem);
      
      if (toggle) {
        const container = toggle.closest(SELECTORS.dropdownContainer);
        if (container) {
          const dropdown = container.querySelector(SELECTORS.dropdownMenu);
          if (dropdown) {
            if (!initializedDropdowns.has(container)) {
              initializeDropdownContainer(container);
            }
            handleDropdownToggle(event, toggle, dropdown);
          }
        }
      } else if (item) {
        const container = item.closest(SELECTORS.dropdownContainer);
        if (container) {
          const dropdown = container.querySelector(SELECTORS.dropdownMenu);
          const containerToggle = container.querySelector(SELECTORS.dropdownToggle);
          if (dropdown && containerToggle) {
            handleDropdownItemClick(event, item, dropdown, containerToggle);
          }
        }
      } else if (!event.target.closest(SELECTORS.dropdownContainer)) {
        closeAllDropdowns();
      }
    }, { passive: false });
    
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const openDropdowns = document.querySelectorAll(
          `${SELECTORS.dropdownMenu}:not([${ARIA_STATES.hidden}])`
        );
        
        if (openDropdowns.length > 0) {
          closeAllDropdowns();
          
          const firstContainer = document.querySelector(SELECTORS.dropdownContainer);
          const firstToggle = firstContainer?.querySelector(SELECTORS.dropdownToggle);
          if (firstToggle) {
            firstToggle.focus();
          }
        }
      }
    }, { capture: false, passive: false });
    
    const observer = new MutationObserver((mutations) => {
      clearSelectorCache();
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches(SELECTORS.dropdownContainer)) {
              initializeDropdownContainer(node);
            } else if (node.querySelector) {
              const containers = node.querySelectorAll(SELECTORS.dropdownContainer);
              containers.forEach(container => {
                initializeDropdownContainer(container);
              });
            }
          }
        });
      });
    });
    
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true
    });

    initializeAllDropdowns();
  }

  /**
   * Closes all "Go to Page" hover sections
   */
  function closeAllGoToSections() {
    const allGoToSections = document.querySelectorAll(SELECTORS.goToHover);
    allGoToSections.forEach(section => {
      section.classList.remove('pagination-go-to--open');
    });
  }

  /**
   * Toggles a "Go to Page" section visibility
   * @param {HTMLElement} goToSection - The go-to section element
   */
  function toggleGoToSection(goToSection) {
    const isOpen = goToSection.classList.contains('pagination-go-to--open');
    
    closeAllGoToSections();
    
    if (!isOpen) {
      goToSection.classList.add('pagination-go-to--open');
      
      const input = goToSection.querySelector(SELECTORS.goToInput);
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }

  /**
   * Handles click on the ellipsis/dots button to toggle "Go to Page" section
   * @param {Event} event - Click event
   */
  function handleGoToToggleClick(event) {
    const toggle = event.target.closest(SELECTORS.goToGapClickable);
    if (!toggle) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const container = toggle.closest(SELECTORS.goToGapItem);
    if (!container) return;
    
    const goToSection = container.querySelector(SELECTORS.goToHover);
    if (!goToSection) return;
    
    toggleGoToSection(goToSection);
  }

  /**
   * Handles click outside to close "Go to Page" sections
   * @param {Event} event - Click event
   */
  function handleGoToClickOutside(event) {
    if (event.target.closest(SELECTORS.goToHover)) return;

    if (event.target.closest(SELECTORS.goToGapClickable)) return;
    
    closeAllGoToSections();
  }

  /**
   * Handles Escape key to close "Go to Page" sections
   * @param {Event} event - Keyboard event
   */
  function handleGoToEscapeKey(event) {
    if (event.key !== 'Escape') return;
    
    const openSections = document.querySelectorAll(`${SELECTORS.goToHover}.pagination-go-to--open`);
    if (openSections.length > 0) {
      closeAllGoToSections();
      
      const firstGapItem = document.querySelector(SELECTORS.goToGapItem);
      const toggle = firstGapItem?.querySelector(SELECTORS.goToGapClickable);
      if (toggle) {
        toggle.focus();
      }
    }
  }

  /**
   * Initializes "Go to Page" section click-based functionality
   */
  function initGoToSectionClickToggle() {
    document.addEventListener('click', handleGoToToggleClick, { capture: true });

    document.addEventListener('click', handleGoToClickOutside, { capture: false });

    document.addEventListener('keydown', handleGoToEscapeKey, { capture: false });
  }

  /**
   * Handles input validation for "Go to page" input
   * @param {Event} event - Input event
   */
  function handleInputValidation(event) {
    const input = event.target;
    
    if (!input.classList.contains('pagination-go-to__input')) {
      return;
    }

    const totalPages = parseInt(input.getAttribute(DATA_ATTRIBUTES.totalPages), 10);
    const value = input.value.trim();

    if (value === '') {
      hideInputError(input);
      return;
    }

    const pageNumber = parseInt(value, 10);

    if (!isValidPageNumber(pageNumber, totalPages)) {
      showInputError(input, totalPages);
    } else {
      hideInputError(input);
    }
  }

  /**
   * Handles Enter key press on "Go to page" input
   * @param {Event} event - Keyboard event
   */
  function handleInputEnterKey(event) {
    if (event.key !== 'Enter') return;

    const input = event.target;
    
    if (!input.classList.contains('pagination-go-to__input')) {
      return;
    }

    event.preventDefault();

    const button = input.nextElementSibling;
    if (button && button.classList.contains('pagination-go-to__button')) {
      handleGoToPage(button);
    }
  }

  /**
   * Initializes all pagination functionality
   */
  function init() {
    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('pagination-go-to__button')) {
        event.preventDefault();
        handleGoToPage(event.target);
        return;
      }
    });

    document.addEventListener('keydown', handleInputEnterKey);
    document.addEventListener('input', handleInputValidation);
  }
  
  initMobileDropdown();
  initGoToSectionClickToggle();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
